import createAdjDetailEntry from './createAdjDetailEntry'
import isReadonlyFatigueAdj from './isReadonlyFatigueAdj'
import calcAbilities from './calcAbilities'
import calcGeneralDetails from './calcGeneralDetails'

import processCalcSkillsHarness from '../processes/processCalcSkillsHarness'
import processCalcTotalsRebuildReasonAppliedAdjsHarness from '../processes/processCalcTotalsRebuildReasonAppliedAdjsHarness'
import processResetTotalsReasonAdjsHarness from '../processes/processResetTotalsReasonAdjsHarness'

import store from '../store/index'

function formatFatigueReason(level, reasonType) {
  return `${level} ${reasonType} Adj`
}

function getHalfMoveAdj(currentMove) {
  const moveValue = Math.max(Number(currentMove) || 0, 0)
  return -(moveValue / 2)
}

function buildExistingAdjMap(adjs = []) {
  return new Map(
    adjs.map((adj) => [
      [adj.name || '', adj.adj_reason || '', adj.adj_value ?? 0, adj.applied === true].join('|'),
      adj,
    ]),
  )
}

function buildFatigueAdjEntry({
  category,
  name,
  adjValue,
  level,
  reasonType,
  existingAdjsByKey,
  applied = false,
}) {
  const adjReason = formatFatigueReason(level, reasonType)
  const existingAdj = existingAdjsByKey.get(
    [name || '', adjReason, adjValue ?? 0, applied === true].join('|'),
  )

  return createAdjDetailEntry({
    id: existingAdj?.id ?? null,
    name,
    adj_reason: adjReason,
    adj_value: adjValue,
    character_item: null,
    editable: false,
    rune: '',
    source: category === 'skill' ? 'character' : category,
    applied,
  })
}

function resetAndRebuildRemovedAdjs({ category, removedAdjs, character }) {
  if (!Array.isArray(removedAdjs) || removedAdjs.length === 0) {
    return character
  }

  character = processResetTotalsReasonAdjsHarness({
    adjs: removedAdjs,
    category,
    character,
  })

  character = processCalcTotalsRebuildReasonAppliedAdjsHarness({
    adjs: removedAdjs,
    category,
    character,
    add_only: false,
  })

  return character
}

function rebuildAddedAdjs({ category, addedAdjs, character }) {
  if (!Array.isArray(addedAdjs) || addedAdjs.length === 0) {
    return character
  }

  return processCalcTotalsRebuildReasonAppliedAdjsHarness({
    adjs: addedAdjs,
    category,
    character,
    add_only: false,
  })
}

function refreshDexDerivedValues(character) {
  character = calcAbilities({ pfx: 'dex', character })
  character = calcGeneralDetails({ character })

  const maxSkill =
    store?.state?.combat?.config?.max_skill ??
    store?.getters?.['combat/getConfig']?.max_skill ??
    100

  if (Array.isArray(character.skills) && character.skills.length > 0) {
    character = processCalcSkillsHarness({
      pfx: 'dex',
      character,
      max_skill: maxSkill,
    })
  }

  if (Array.isArray(character.characteristics)) {
    const dexCharacteristic = character.characteristics.find((entry) => entry?.name === 'dex')

    if (dexCharacteristic) {
      dexCharacteristic.cur = character.dex_cur
    }
  }

  return character
}

export default function syncCharacterFatigueAdjustments(character, fatigueDetails = {}) {
  if (!character || typeof character !== 'object') {
    return character
  }

  character.ability_adjs = Array.isArray(character.ability_adjs) ? character.ability_adjs : []
  character.characteristic_adjs = Array.isArray(character.characteristic_adjs)
    ? character.characteristic_adjs
    : []
  character.skills = Array.isArray(character.skills) ? character.skills : []
  character.skill_adjs = Array.isArray(character.skill_adjs) ? character.skill_adjs : []

  const removedAbilityAdjs = character.ability_adjs.filter((adj) =>
    isReadonlyFatigueAdj(adj?.adj_reason),
  )
  const removedCharacteristicAdjs = character.characteristic_adjs.filter((adj) =>
    isReadonlyFatigueAdj(adj?.adj_reason),
  )
  const removedSkillAdjs = character.skill_adjs.filter((adj) =>
    isReadonlyFatigueAdj(adj?.adj_reason),
  )

  const abilityAdjMap = buildExistingAdjMap(removedAbilityAdjs)
  const characteristicAdjMap = buildExistingAdjMap(removedCharacteristicAdjs)
  const skillAdjMap = buildExistingAdjMap(removedSkillAdjs)

  character.ability_adjs = character.ability_adjs.filter(
    (adj) => !isReadonlyFatigueAdj(adj?.adj_reason),
  )
  character.characteristic_adjs = character.characteristic_adjs.filter(
    (adj) => !isReadonlyFatigueAdj(adj?.adj_reason),
  )
  character.skill_adjs = character.skill_adjs.filter(
    (adj) => !isReadonlyFatigueAdj(adj?.adj_reason),
  )

  character = resetAndRebuildRemovedAdjs({
    category: 'ability',
    removedAdjs: removedAbilityAdjs,
    character,
  })
  character = resetAndRebuildRemovedAdjs({
    category: 'characteristic',
    removedAdjs: removedCharacteristicAdjs,
    character,
  })
  character = resetAndRebuildRemovedAdjs({
    category: 'skill',
    removedAdjs: removedSkillAdjs,
    character,
  })

  const nextLevel = fatigueDetails?.level || character.fatigue || 'Fresh'

  const addedAbilityAdjs = []
  const addedCharacteristicAdjs = []
  const addedSkillAdjs = []

  if (nextLevel !== 'Fresh') {
    const srAdj = Number(fatigueDetails?.adj_sr) || 0
    if (srAdj !== 0) {
      const entry = buildFatigueAdjEntry({
        category: 'ability',
        name: 'sr',
        adjValue: srAdj,
        level: nextLevel,
        reasonType: 'SR',
        existingAdjsByKey: abilityAdjMap,
      })
      character.ability_adjs.push(entry)
      addedAbilityAdjs.push(entry)
    }

    let moveAdj = Number(fatigueDetails?.adj_move) || 0
    let moveFlyAdj = Number(fatigueDetails?.adj_move) || 0
    if (Number(fatigueDetails?.adj_move) === -99) {
      moveAdj = getHalfMoveAdj(character.move_cur)
      moveFlyAdj = getHalfMoveAdj(character.move_fly_cur)
    }

    if (moveAdj !== 0) {
      const entry = buildFatigueAdjEntry({
        category: 'ability',
        name: 'move',
        adjValue: moveAdj,
        level: nextLevel,
        reasonType: 'Move',
        existingAdjsByKey: abilityAdjMap,
      })
      character.ability_adjs.push(entry)
      addedAbilityAdjs.push(entry)
    }

    if (moveFlyAdj !== 0) {
      const entry = buildFatigueAdjEntry({
        category: 'ability',
        name: 'move_fly',
        adjValue: moveFlyAdj,
        level: nextLevel,
        reasonType: 'Move',
        existingAdjsByKey: abilityAdjMap,
      })
      character.ability_adjs.push(entry)
      addedAbilityAdjs.push(entry)
    }

    const dexAdj = Number(fatigueDetails?.adj_dex) || 0
    if (dexAdj !== 0) {
      const entry = buildFatigueAdjEntry({
        category: 'characteristic',
        name: 'dex',
        adjValue: dexAdj,
        level: nextLevel,
        reasonType: 'Dex',
        existingAdjsByKey: characteristicAdjMap,
      })
      character.characteristic_adjs.push(entry)
      addedCharacteristicAdjs.push(entry)
    }

    const skillAdj = Number(fatigueDetails?.adj_skills) || 0
    if (skillAdj !== 0) {
      const skillNames = [
        ...new Set((character.skills || []).map((skill) => skill?.name).filter(Boolean)),
      ]
      skillNames.forEach((skillName) => {
        const entry = buildFatigueAdjEntry({
          category: 'skill',
          name: skillName,
          adjValue: skillAdj,
          level: nextLevel,
          reasonType: 'Skill',
          existingAdjsByKey: skillAdjMap,
          applied: true,
        })
        character.skill_adjs.push(entry)
        addedSkillAdjs.push(entry)
      })
    }
  }

  character = rebuildAddedAdjs({
    category: 'ability',
    addedAdjs: addedAbilityAdjs,
    character,
  })
  character = rebuildAddedAdjs({
    category: 'characteristic',
    addedAdjs: addedCharacteristicAdjs,
    character,
  })
  character = rebuildAddedAdjs({
    category: 'skill',
    addedAdjs: addedSkillAdjs,
    character,
  })

  if (removedCharacteristicAdjs.length > 0 || addedCharacteristicAdjs.length > 0) {
    character = refreshDexDerivedValues(character)
  }

  return character
}
